import type { WPPostListItem } from '@ria-client/supergraph/structuredContent/hydration/WPPostList/index';
import { ChevronRight } from 'lucide-react';
import Image from 'next/image';
import { ContentLink } from './content-link';

export interface NextStepsCardsProps {
  items: WPPostListItem[];
}

/**
 * `WPPostList` `next-steps` template as a static row. Legacy is a react-slick carousel: 4 slides
 * from 1280px, 270px scrollable slides below. Arrows are dropped; the row scrolls natively.
 * Source: NerdWallet/structured-content shared-core/src/components/post-list/templates/next-steps.
 */
export function NextStepsCards({ items }: NextStepsCardsProps) {
  return (
    <ul className="flex snap-x gap-4 overflow-x-auto lg:grid lg:grid-cols-4 lg:overflow-visible">
      {items.map(item => (
        <li className="w-[270px] shrink-0 snap-start lg:w-auto" key={item.id}>
          {/* 270px card height, py 24 / px 16, margin 4: next-steps.module.less `.card`. */}
          <ContentLink
            className="group m-1 flex h-[270px] flex-col justify-between gap-7 rounded-card bg-background px-4 py-6 text-foreground no-underline transition-shadow duration-500 hover:no-underline hover:shadow-review-card sm:gap-8"
            href={item.url ?? undefined}
          >
            <div>
              <p className="typography-text-bold line-clamp-3">{item.title}</p>
              {item.synopsis ? (
                <p className="typography-text-small line-clamp-4">{item.synopsis}</p>
              ) : null}
            </div>
            <div
              className={`flex items-end gap-2 ${item.authors?.length ? 'justify-between' : 'justify-end'}`}
            >
              <Authors authors={item.authors ?? []} />
              {/* #02427e on hover: legacy next-steps.module.less `.arrow` background-blue-dark. */}
              <span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary-foreground transition-colors duration-500 group-hover:bg-[var(--content-blue-dark)]">
                <ChevronRight aria-hidden="true" className="size-5 text-primary-foreground" />
              </span>
            </div>
          </ContentLink>
        </li>
      ))}
    </ul>
  );
}

// next-steps/authors.tsx: first author's 48px avatar at 32px, a count badge, names clamped to 2 lines.
function Authors({ authors }: { authors: NonNullable<WPPostListItem['authors']> }) {
  if (!authors.length) return null;
  const avatar = authors[0]?.avatarUrls?.[0]?.url;

  return (
    <div className="flex gap-3" title={authors.map(author => author.name).join(', ')}>
      <div className="relative shrink-0">
        {avatar ? (
          <Image
            alt={`${authors[0]?.name}'s profile picture`}
            className="size-8 rounded-full"
            height={32}
            src={avatar}
            width={32}
          />
        ) : null}
        {/* Counter badge: authors.module.less `.authorCounter` (neutral-lighter-2 bg, green-darkest text). */}
        {authors.length > 1 ? (
          <span className="typography-text-extra-small absolute -right-2 bottom-0 flex size-5 items-center justify-center rounded-full border-2 border-background bg-[var(--content-neutral-lighter-2)] text-[var(--content-green-darkest)]">
            {authors.length}
          </span>
        ) : null}
      </div>
      {/* #3d4045: legacy neutral-darker-2; 14px line-height: authors.module.less `.authorNames`. */}
      <span className="typography-text-extra-small line-clamp-2 self-center !leading-[14px] text-[var(--content-neutral-darker-2)]">
        {authors.length > 1 ? 'By ' : ''}
        {authors.map(author => author.name).join(', ')}
      </span>
    </div>
  );
}
